import { useState } from "react";
import { useAuthRoles } from "../hooks/useAuthRoles";
import { extractIdFromIri } from "../utils/extractFromIri";

/**
 * @param {Array} initialData
 * @param {Object} options
 * @param {number} options.initialPageIndex
 */
export function useTableControls(
  initialData = [],
  { initialPageIndex = 1 } = {},
) {
  const [tableData, setTableData] = useState(initialData);
  const [pendingSearchTerm, setPendingSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: initialPageIndex,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [open, setOpen] = useState(false);
  const { isProvider, decoded, isAdmin } = useAuthRoles();

  const participantId = decoded?.participant_id
    ? String(decoded.participant_id).trim()
    : null;

  const hasParticipantId = (obj) => {
    if (!obj || !participantId) return false;

    const nestedPublishers = Array.isArray(obj.items)
      ? obj.items.map((item) => item?.metadata?.publisher)
      : [];

    const idsToCheck = [
      extractIdFromIri(obj.publisher),
      extractIdFromIri(obj.assigner),
      obj.publisher,
      obj.assigner,
      ...nestedPublishers.map((value) => extractIdFromIri(value)),
      ...nestedPublishers,
    ];

    return idsToCheck.some((id) => String(id || "").trim() === participantId);
  };

  const orgProvider =
    isAdmin || (isProvider && tableData.some((row) => hasParticipantId(row)));

  const handleSearch = (e) => setPendingSearchTerm(e.target.value);
  const handleClearSearch = () => {
    setPendingSearchTerm("");
    setSearchTerm("");
  };
  const handleSearchSubmit = () => setSearchTerm(pendingSearchTerm);

  const resetPagination = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: initialPageIndex,
    }));
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleApplyFilters = (newFilters) => setFilters(newFilters);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  return {
    tableData,
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    orgProvider,
    pagination,
    rowCount,
    resetPagination,
    setOpen,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleOpen,
    handleClose,
    handleSearchKeyDown,
    handleApplyFilters,
    setFilters,
    setTableData,
    setPagination,
    setRowCount,
  };
}
